import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

type FeatureAccessMap = Record<string, boolean>;

type FeatureAccessContextType = {
  checkAccess: (featureName: string) => Promise<boolean>;
  subscription: {
    tier: string;
    status: string;
    daysRemaining?: number;
  } | null;
  betaTester: boolean;
  loading: boolean;
};

const FeatureAccessContext = createContext<FeatureAccessContextType | null>(null);

export const FeatureAccessProvider = ({ children }: { children: React.ReactNode }) => {
  const [cache, setCache] = useState<FeatureAccessMap>({});
  const [subscription, setSubscription] = useState<FeatureAccessContextType['subscription']>(null);
  const [betaTester, setBetaTester] = useState(false);
  const [loading, setLoading] = useState(true);

  // We need to only call useNavigate inside a Router context
  let navigate: ReturnType<typeof useNavigate> | null = null;
  try {
    navigate = useNavigate();
  } catch {
    // Suppress the error for environments outside the Router
    navigate = null;
  }

  useEffect(() => {
    const fetchAccessInfo = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('Session error:', sessionError);
          if (navigate) navigate('/login');
          return;
        }

        if (!session?.user) {
          console.log('No user found in session');
          if (navigate) navigate('/login');
          return;
        }

        const userId = session.user.id;

        const { data: sub, error: subError } = await supabase
          .from('subscriptions')
          .select(`
            tier_id,
            status,
            start_date,
            end_date,
            subscription_tiers (
              name
            )
          `)
          .eq('user_id', userId)
          .maybeSingle();

        if (subError) {
          console.error('Error fetching subscription:', subError);
        } else if (sub) {
          const daysRemaining = sub.end_date
            ? Math.ceil((new Date(sub.end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
            : undefined;

          setSubscription({
            tier: sub.subscription_tiers.name,
            status: sub.status,
            daysRemaining
          });
        }

        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('beta_tester')
          .eq('id', userId)
          .maybeSingle();

        if (profileError) {
          console.error('Error fetching beta_tester flag:', profileError);
        } else {
          setBetaTester(!!profile?.beta_tester);
        }

      } catch (error) {
        console.error('Unexpected error during feature access fetch:', error);
        if (navigate) navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    fetchAccessInfo();
  }, [navigate]);

  const checkAccess = async (featureName: string): Promise<boolean> => {
    if (featureName in cache) return cache[featureName];

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        console.log('No session/user when checking feature');
        return false;
      }

      const { data, error } = await supabase.rpc('check_feature_access', {
        p_user_id: session.user.id,
        p_feature_name: featureName
      });

      if (error) {
        console.error('Error checking feature access:', error);
        return false;
      }

      const result = !!data;
      setCache(prev => ({ ...prev, [featureName]: result }));
      return result;
    } catch (error) {
      console.error('Error in checkAccess:', error);
      return false;
    }
  };

  return (
    <FeatureAccessContext.Provider value={{ checkAccess, subscription, betaTester, loading }}>
      {children}
    </FeatureAccessContext.Provider>
  );
};

export const useFeatureAccess = (featureName: string) => {
  const context = useContext(FeatureAccessContext);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!context) return;

    const checkFeatureAccess = async () => {
      try {
        const result = await context.checkAccess(featureName);
        setHasAccess(result);
      } catch (error) {
        console.error('Error checking feature access:', error);
        setHasAccess(false);
      } finally {
        setLoading(false);
      }
    };

    checkFeatureAccess();
  }, [featureName, context]); 

  if (!context) {
    throw new Error('useFeatureAccess must be used within a FeatureAccessProvider');
  }

  return {
    hasAccess,
    loading,
    subscription: context.subscription,
    betaTester: context.betaTester,
    subscriptionLoading: context.loading
  };
};
